import { FaroChip, FaroText } from "@faro-lotv/flat-ui";
import { Grid, Stack } from "@mui/material";
import { sphereColors } from "@styles/common-colors";
import { StripeCheckout } from "@pages/settings/subscription-settings/credits/stripe-checkout";
import FaroSphereLogo from "@assets/icons/faro-sphere-xg-logo-color.svg?react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { ReactSetStateFunction } from "@custom-types/types";
import LeftArrow from "@assets/icons/new/arrow-left-2_16px.svg?react";
import { FaroIconButton } from "@components/common/faro-icon-button";
import { SphereDivider } from "@components/common/sphere-divider";
import { CREDITS_DISPLAY_NAME } from "@src/constants/credit-constants";
import { runtimeConfig } from "@src/runtime-config";
import { useEffect, useMemo } from "react";
import { formatStripeDisplayPrice } from "@utils/stripe-product-utils";
import { FaroCoreApiTypes } from "@stellar/core-api-client";

interface Props {
  /** The credit package to be purchased */
  creditPackage: FaroCoreApiTypes.StripeProduct;

  /** Callback set state function return to credits page */
  setShouldCheckout: ReactSetStateFunction<boolean>;
}

/** Renders the credit checkout page showing package details and Stripe payment form */
export function CreditCheckoutPage({
  creditPackage,
  setShouldCheckout,
}: Props): JSX.Element {
  const priceToShow = formatStripeDisplayPrice(creditPackage.price.price);

  /** Memorized stripe promise to create the payment element */
  const stripePromise = useMemo(
    () => loadStripe(runtimeConfig.features.stripePublishableKey),
    []
  );

  // Prevent the user from leaving the page when the payment is in progress
  useEffect(() => {
    function handleBeforeUnload(event: BeforeUnloadEvent): void {
      event.preventDefault();
      event.returnValue = "";
    }

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  return (
    <Grid container width="100%" height="100vh">
      <Grid
        item
        xs={12}
        sm={12}
        md={6}
        lg={6}
        xl={6}
        component="div"
        sx={{ backgroundColor: sphereColors.gray50 }}
      >
        <Stack
          sx={{
            alignItems: "start",
            width: { xs: "80%", sm: "80%", md: "70%" },
            marginTop: { xs: "50px", sm: "50px", md: "150px" },
            marginX: "auto",
          }}
        >
          <Stack
            sx={{ flexDirection: "row", alignItems: "center", mb: "25px" }}
          >
            <FaroIconButton
              component={LeftArrow}
              onClick={() => setShouldCheckout(false)}
              iconProps={{
                sx: {
                  color: sphereColors.gray600,
                },
              }}
            />
            <FaroSphereLogo
              style={{
                width: "auto",
                height: "40px",
                marginLeft: "6px",
              }}
            />
          </Stack>
          <Stack sx={{ padding: "30px 10px", width: "100%" }}>
            <FaroText variant="heading16" color={sphereColors.gray600}>
              Add {CREDITS_DISPLAY_NAME}
            </FaroText>
            <Stack
              sx={{
                flexDirection: "row",
                alignItems: "flex-end",
                marginBottom: "40px",
                gap: 1,
              }}
            >
              <FaroText variant="heading32" color={sphereColors.gray800}>
                {priceToShow}
              </FaroText>
              <FaroChip
                label="12 months"
                variant="outlined"
                color={sphereColors.gray600}
                backgroundColor="transparent"
                sx={{ marginBottom: "8px" }}
              />
            </Stack>
            <Stack
              sx={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <FaroText variant="bodyM">{creditPackage.name}</FaroText>
              <FaroText variant="bodyM">{priceToShow}</FaroText>
            </Stack>

            <SphereDivider
              sx={{ borderColor: sphereColors.gray200, my: "12px" }}
            />

            <Stack
              sx={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <FaroText variant="bodyM">Total</FaroText>
              <FaroText variant="bodyM">{priceToShow}</FaroText>
            </Stack>
          </Stack>
        </Stack>
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={6} xl={6} component="div">
        <Stack
          sx={{
            alignItems: "center",
            marginTop: { xs: "50px", sm: "50px", md: "150px" },
          }}
        >
          <Stack sx={{ width: { xs: "80%", sm: "80%", md: "70%" } }}>
            <FaroText variant="heading16" marginBottom={2}>
              Pay with card
            </FaroText>
            <Elements
              stripe={stripePromise}
              options={{
                mode: "payment",
                amount: parseFloat(creditPackage.price.price),
                currency: creditPackage.price.currency,
                paymentMethodTypes: ["card"],
                appearance: {
                  variables: {
                    colorPrimary: sphereColors.blue500,
                    spacingUnit: "0.3rem",
                  },
                },
              }}
            >
              <StripeCheckout creditPackage={creditPackage} />
            </Elements>
          </Stack>
        </Stack>
      </Grid>
    </Grid>
  );
}
